import { Controller } from "@hotwired/stimulus"

/**
 * Accessible HTML legend for a Chartkick pie/doughnut chart. — LRPO-5188
 *
 * WHY THIS EXISTS (WCAG 2.1.1 Keyboard + 4.1.2 Name/Role/Value):
 * Chart.js renders its default legend ONTO the <canvas>. Those legend hit-areas
 * are not DOM nodes, so they are unreachable by Tab, expose no role/name/state
 * to assistive tech, and the only way to toggle a slice is a mouse click on the
 * canvas. This controller replaces that canvas legend with REAL focusable
 * <button> elements — one per data index — each carrying:
 *   - an accessible name (the visible slice label),
 *   - aria-pressed reflecting current slice VISIBILITY (true = shown),
 * and operable with Tab + Enter/Space because a native <button> fires `click`
 * for both mouse AND keyboard activation (so we add NO keydown handler).
 *
 * On the dashboard the canvas legend is turned OFF in the view
 * (`plugins: { legend: { display: false } }`), so this is the ONLY legend —
 * there is never a duplicate legend on screen.
 *
 * CONVERGENCE WITH THE LIVE COUNTER (AC #3):
 * After each toggle we dispatch a bubbling `chart-legend:toggled` CustomEvent
 * from the controller element. The donut total overlay controller listens for
 * it (on its own element, which is an ancestor of this one) and recomputes the
 * "Total Leads" value inside its aria-live region — so a keyboard toggle drives
 * the screen-reader announcement, not just a mouse click on the canvas.
 *
 * USAGE (markup):
 *   <div data-controller="chart-legend"
 *        data-chart-legend-chart-id-value="leads-pie-chart"
 *        data-chart-legend-label-value="Mobile User Leads split"></div>
 * The buttons render directly into `this.element` (no separate target needed —
 * the element IS the legend container; this keeps the markup a single node so it
 * can sit inside the donut-total-overlay wrapper and let the event bubble up).
 * We give `this.element` `role="group"` + an aria-label naming the chart.
 *
 * RESILIENCE (mirrors donut_total_overlay_controller.js):
 *   - Chartkick renders async; if the chart isn't in the registry yet we retry
 *     via requestAnimationFrame, bounded by MAX_READY_ATTEMPTS so a never-
 *     rendered chart (no-data branch) can't spin forever.
 *   - On EACH activation we re-resolve the chart fresh from the registry (like
 *     the orphaned _toggleable_points_legend's getLiveChart) so a Chartkick
 *     rebuild — destroy() + new ChartClass() — can't strand a dead reference.
 *   - disconnect() cancels any pending rAF and nulls refs; reconnect is safe.
 */
const MAX_READY_ATTEMPTS = 60 // ~1s at 60fps before we give up cleanly

export default class extends Controller {
  static values = {
    chartId: String,
    label: { type: String, default: "" }
  }

  connect() {
    this._attempts = 0
    this._rafId = null
    this._renderRafId = null
    this._tryRender()

    // LRPO-5188 ghost-rebuild resilience. chartkick_turbo_lifecycle.js rebuilds
    // the dashboard donut IN PLACE on turbo:render (after Chartkick.destroyAll)
    // for redirect-chain / masquerade / back-forward visits — WITHOUT a Stimulus
    // disconnect. The rebuilt chart is all-visible, but our buttons keep their
    // stale aria-pressed/.is-hidden (drift vs the live chart). Re-render on
    // turbo:render so every slice's aria-pressed reflects the fresh visibility.
    //
    // CRITICAL: defer to requestAnimationFrame. The lifecycle's rebuild runs
    // synchronously inside the SAME turbo:render dispatch, so the listener
    // ordering is a race — but by the next frame the rebuilt chart is
    // guaranteed in the registry regardless of which listener ran first.
    //
    // We re-render ONLY when the resolved chart is a DIFFERENT instance than the
    // one we currently have rendered (this._chart) — a normal Drive render that
    // didn't rebuild this chart leaves the same instance, so we no-op (no
    // needless DOM churn / focus loss). Strictly scoped to this controller's
    // chartId via _resolveChart.
    //
    // NOTE: re-rendering calls replaceChildren(), so if a legend button had
    // keyboard focus at rebuild time, focus resets to <body>. That's acceptable
    // here: a ghost-rebuild is a navigation-class event where focus reset is
    // expected. Focus restoration is deferred to the app-wide-sweep follow-up.
    this._onTurboRender = () => {
      if (this._renderRafId != null) window.cancelAnimationFrame(this._renderRafId)
      this._renderRafId = window.requestAnimationFrame(() => {
        this._renderRafId = null
        const fresh = this._resolveChart()
        if (!fresh) return
        if (fresh === this._chart) return // same instance — nothing rebuilt
        this._chart = fresh
        this._render(fresh)
      })
    }
    document.addEventListener("turbo:render", this._onTurboRender)
  }

  disconnect() {
    if (this._rafId != null) {
      window.cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
    if (this._renderRafId != null) {
      window.cancelAnimationFrame(this._renderRafId)
      this._renderRafId = null
    }
    if (this._onTurboRender) {
      document.removeEventListener("turbo:render", this._onTurboRender)
      this._onTurboRender = null
    }
    this._chart = null
  }

  // Poll the Chartkick registry until the Chart.js instance exists, then render
  // the legend once. Bails cleanly after MAX_READY_ATTEMPTS.
  _tryRender() {
    const chart = this._resolveChart()
    if (chart) {
      this._chart = chart
      this._render(chart)
      return
    }
    if (this._attempts >= MAX_READY_ATTEMPTS) return
    this._attempts += 1
    this._rafId = window.requestAnimationFrame(() => this._tryRender())
  }

  // Reach the underlying Chart.js instance from Chartkick's registry. Returns
  // null (never throws) if Chartkick, the registry entry, getChartObject, or
  // the chart's data isn't present yet. Resolved on connect AND on each toggle
  // so a rebuilt chart never strands a dead reference.
  _resolveChart() {
    const registry = window.Chartkick && window.Chartkick.charts
    const entry = registry && registry[this.chartIdValue]
    if (!entry || typeof entry.getChartObject !== "function") return null
    const chart = entry.getChartObject()
    return chart && chart.data ? chart : null
  }

  // Per-index visibility, with a safe fallback for chart types/builds that
  // don't expose getDataVisibility (treat as visible).
  _isVisible(chart, index) {
    return typeof chart.getDataVisibility === "function"
      ? chart.getDataVisibility(index)
      : true
  }

  // Build one <button> per label and mount them into this.element. Idempotent:
  // clears any prior render so a reconnect/rebuild can't stack two legends.
  _render(chart) {
    const labels = (chart.data && chart.data.labels) || []
    const dataset = (chart.data && chart.data.datasets && chart.data.datasets[0]) || {}
    const bg = dataset.backgroundColor

    // Container semantics: a labelled group so SR users hear "Toggle <chart>
    // series, group" before tabbing through the buttons.
    // replaceChildren() (no args) clears children without ever parsing an HTML
    // string — XSS-proof by construction. Labels/colors below go in via
    // textContent / createElement only, never innerHTML.
    this.element.replaceChildren()
    this.element.classList.add("chart-legend")
    this.element.setAttribute("role", "group")
    // Group label carries the "press to show or hide" hint ONCE (not per
    // button) so a screen-reader user learns what aria-pressed means here —
    // pressed = the slice is shown — without repeating it on every Tab. Each
    // button's own accessible name stays the bare slice label.
    const subject = this.labelValue ? `${this.labelValue} series` : "chart series"
    this.element.setAttribute(
      "aria-label",
      `Toggle ${subject} — press each to show or hide its slice`
    )

    labels.forEach((label, index) => {
      const color = Array.isArray(bg) ? bg[index] : (typeof bg === "string" ? bg : null)
      const visible = this._isVisible(chart, index)

      const button = document.createElement("button")
      button.type = "button"
      button.className = "chart-legend__item"
      button.dataset.index = String(index)
      // aria-pressed reflects VISIBILITY: pressed/true = slice shown.
      button.setAttribute("aria-pressed", visible ? "true" : "false")
      if (!visible) button.classList.add("is-hidden")

      // Color swatch — decorative only; aria-hidden so the button's accessible
      // name is exactly the label text (no color noise read aloud). Hidden
      // state is conveyed by aria-pressed + opacity + line-through, never by
      // color alone (WCAG 1.4.1).
      const swatch = document.createElement("span")
      swatch.className = "chart-legend__swatch"
      swatch.setAttribute("aria-hidden", "true")
      if (color) swatch.style.backgroundColor = color

      const text = document.createElement("span")
      text.className = "chart-legend__label"
      text.textContent = String(label)

      button.appendChild(swatch)
      button.appendChild(text)
      // Native <button> click covers mouse AND keyboard (Enter/Space) — no
      // manual keydown handler needed (would double-fire on Space otherwise).
      button.addEventListener("click", () => this._toggle(index, button))

      this.element.appendChild(button)
    })
  }

  // Toggle one slice's visibility, then re-read the canonical state and sync the
  // button. Re-resolves the chart fresh so a rebuild can't act on a dead ref.
  _toggle(index, button) {
    const chart = this._resolveChart()
    if (!chart) return
    this._chart = chart

    if (typeof chart.toggleDataVisibility === "function") {
      chart.toggleDataVisibility(index)
    }
    if (typeof chart.update === "function") chart.update()

    // Read back the TRUE post-toggle state (don't assume the flip) and mirror
    // it onto the button so aria-pressed/.is-hidden never drift from the chart.
    const visible = this._isVisible(chart, index)
    button.setAttribute("aria-pressed", visible ? "true" : "false")
    button.classList.toggle("is-hidden", !visible)

    // Convergence point: let the donut total overlay (an ancestor) recompute
    // the live "Total Leads" region. Bubbles so a sibling/ancestor controller
    // can hear it regardless of nesting.
    this.dispatch("toggled", {
      bubbles: true,
      detail: { chartId: this.chartIdValue, index }
    })
  }
}
